import { createSelector } from "@reduxjs/toolkit";
import { scheduling } from "lobbie";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    ACCOUNT_PUBLIC_UUID,
    DEFAULT_PATIENT_SCHEDULING,
    SELF_SCHEDULING_SETTING_PUBLIC_UUID,
} from "src/constants";
import {
    useLobbieAccountPublicUuid,
    useLobbieSelfSchedulingSettingPublicUuid,
} from "src/hooks/accounts/accounts";
import { useLogoutNoRedirect } from "src/hooks/user";
import { IAppState } from "src/redux";
import {
    resetPatientScheduling,
    setPatientScheduling,
} from "src/redux/actions/patientSchedulingActions";
import { logDev, notify, removeAllUserSessionData } from "src/utils";

const schedulingState = (state: IAppState) => state;

const schedulingSelector = createSelector(
    [schedulingState],
    (_schedulingState: IAppState) => _schedulingState.patientScheduling,
);

export const usePatientSelfScheduling = () => {
    const currentScheduling = useSelector(schedulingSelector) || DEFAULT_PATIENT_SCHEDULING;
    const dispatch = useDispatch();
    const logout = useLogoutNoRedirect(true);

    const accountPublicUuid = useLobbieAccountPublicUuid();
    const selfSchedulingSettingPublicUuid = useLobbieSelfSchedulingSettingPublicUuid();

    const dispatchScheduling = useCallback(
        (patientScheduling: Partial<scheduling.patient.IPatientScheduling>) => {
            dispatch(setPatientScheduling(patientScheduling));
        },
        [dispatch],
    );

    const dispatchReset = useCallback(() => {
        logDev("PatientSchedulingHomeV2.dispatchReset - Reset patient scheduling and start flow");
        dispatch(resetPatientScheduling());
    }, [dispatch]);

    const dispatchExpire = useCallback(async () => {
        removeAllUserSessionData();
        await logout().catch(console.error);

        if (accountPublicUuid && selfSchedulingSettingPublicUuid) {
            setTimeout(() => {
                window.location.href = `/patient/scheduling?${ACCOUNT_PUBLIC_UUID}=${accountPublicUuid}&${SELF_SCHEDULING_SETTING_PUBLIC_UUID}=${selfSchedulingSettingPublicUuid}`;
            }, 3000);
        }
        notify({
            level: "warning",
            title: "Scheduling expired.",
            message: accountPublicUuid
                ? "Restarting appointment booking."
                : "Please restart this booking.",
        });
    }, [logout, accountPublicUuid, selfSchedulingSettingPublicUuid]);

    return {
        scheduling: currentScheduling,
        setScheduling: dispatchScheduling,
        reset: dispatchReset,
        expire: dispatchExpire,
        accountPublicUuid: currentScheduling.account?.publicUuid || accountPublicUuid,
        selfSchedulingSettingPublicUuid:
            currentScheduling.selfSchedulingSetting.publicUuid || selfSchedulingSettingPublicUuid,
    };
};
